import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import { AsyncThunkConfig, RootState } from "./store";
import { districtsAPI } from "../app/api";
import { AppStatusType } from "./appStatusReducer";
import { DistrictFormFieldsType, DistrictType } from "../types/districtsTypes";
import { SignOutThunk } from "./userReducer";
import { message } from "antd";

interface InitialStateType {
    districts: DistrictType[]
    defaultDistricts: DistrictType[]
    currentDistrict: DistrictType | null
    currentPage: number
    isDistrictsLoading: boolean
    organizationOptions: {value: number, label: string}[]
}

const allOrganizationOption = {value: 0, label: 'Alle Bezirksobjekt'}

const initialState: InitialStateType = {
    districts: [],
    defaultDistricts: [],
    currentDistrict: null,
    currentPage: 0,
    isDistrictsLoading: false,
    organizationOptions: [allOrganizationOption]
}

export const districtsSlice = createSlice({
    name: 'districts',
    initialState,
    reducers: {
        onSetDistricts: (state, action: PayloadAction<DistrictType[]>) => {
            state.districts = action.payload
            state.defaultDistricts = action.payload
        },
        onSetGridRows: (state, action: PayloadAction<DistrictType[]>) => {
            state.districts = action.payload
        },
        onSetCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload
        },
        onSetIsDistrictsLoading: (state, action: PayloadAction<boolean>) => {
            state.isDistrictsLoading = action.payload
        },
        onSetCurrentDistrict: (state, action: PayloadAction<DistrictType | null>) => {
            state.currentDistrict = action.payload
        },
        onSetOrganizationOptions: (state, action: PayloadAction<{value: number, label: string}[]>) => {
            state.organizationOptions = [{value: 0, label: 'All'}, ...action.payload]
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(GetDistrictsList.fulfilled, (state, action) => {
                state.districts = action.payload
                state.defaultDistricts = action.payload
                state.organizationOptions = action.payload.reduce<{value: number, label: string}[]>((acc, district) => {
                    const {id, name} = district.organization
                    const alreadyAdded = acc.some((org) => org.value === id)
                    if (!alreadyAdded) {
                        acc.push({value: id, label: name})
                    }
                    return acc
                }, [allOrganizationOption])
            })
            .addCase(GetDistrictById.fulfilled, (state, action) => {
                state.currentDistrict = action.payload
            })
            .addCase(DeleteDistrict.fulfilled, (state, action) => {
                const filteredDistricts = state.districts.filter((district) => district.id !== action.payload)
                state.districts = filteredDistricts
                state.defaultDistricts = state.defaultDistricts.filter((district) => district.id !== action.payload)
                state.organizationOptions = filteredDistricts.reduce<{value: number, label: string}[]>((acc, district) => {
                    const {id, name} = district.organization
                    const alreadyAdded = acc.some((org) => org.value === id)
                    if (!alreadyAdded) {
                        acc.push({value: id, label: name})
                    }
                    return acc
                }, [allOrganizationOption])
            })
            .addCase(SignOutThunk.fulfilled, (state) => {
                state.districts = []
                state.defaultDistricts = []
                state.currentPage = 0
                state.isDistrictsLoading = false
                state.organizationOptions = []
            })
    }
})

export const {
    onSetDistricts,
    onSetGridRows,
    onSetCurrentPage,
    onSetIsDistrictsLoading,
    onSetCurrentDistrict,
    onSetOrganizationOptions
} = districtsSlice.actions

export const selectDistrictsGridRows = (state: RootState): DistrictType[] => state.districts.districts
export const selectDefaultDistrictsGridRows = (state: RootState): DistrictType[] => state.districts.defaultDistricts
export const selectCurrentPage = (state: RootState): number => state.districts.currentPage
export const selectIsDistrictsLoading = (state: RootState): boolean => state.districts.isDistrictsLoading
export const selectCurrentDistrict = (state: RootState): DistrictType | null => state.districts.currentDistrict
export const selectOrganizationOptions = (state: RootState): {value: number, label: string}[] => state.districts.organizationOptions

export const GetDistrictsList = createAsyncThunk<DistrictType[], void, AsyncThunkConfig>(
    'districts/getDistrictsList',
    async (_, thunkAPI) => {
        thunkAPI.dispatch(onSetIsDistrictsLoading(true))
        try {
            const {status, data} = await districtsAPI.getDistricts()
            thunkAPI.dispatch(onSetIsDistrictsLoading(false))
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data.district_objects, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data.district_objects)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const GetDistrictById = createAsyncThunk<DistrictType, number, AsyncThunkConfig>(
    'districts/getDistrictById',
    async (id, thunkAPI) => {
        thunkAPI.dispatch(onSetIsDistrictsLoading(true))
        try {
            const {status, data} = await districtsAPI.getDistrictById(id)
            thunkAPI.dispatch(onSetIsDistrictsLoading(false))
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const UpdateDistrict = createAsyncThunk<DistrictType, {id: number, formValues: DistrictFormFieldsType}, AsyncThunkConfig>(
    'districts/updateDistrict',
    async (params, thunkAPI) => {
        thunkAPI.dispatch(onSetIsDistrictsLoading(true))
        try {
            const {status, data} = await districtsAPI.updateDistrict(params)
            thunkAPI.dispatch(onSetIsDistrictsLoading(false))
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data, {appStatus: AppStatusType.succeeded, appMessage: 'Der Bezirk wurde erfolgreich aktualisiert'})
            } else {
                return thunkAPI.rejectWithValue(data)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const DeleteDistrict = createAsyncThunk<number, number, AsyncThunkConfig>(
    'districts/deleteDistrict',
    async (districtId, thunkAPI) => {
        try {
            const {status} = await districtsAPI.deleteDistrict(districtId)
            if (status === 200) {
                message.success('District was successfully deleted')
                return thunkAPI.fulfillWithValue(districtId, {appStatus: AppStatusType.idle})
            } else {
                message.error('Failed to delete district')
                return thunkAPI.rejectWithValue(districtId)
            }
        } catch (error: any) {
            message.error(error?.response?.data?.message)
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export default districtsSlice.reducer
